/**
 * @fileoverview Order detail page component for displaying and managing individual orders.
 *
 * This module provides:
 * - Comprehensive order information display
 * - File upload functionality with drag-and-drop support
 * - Order status tracking and action buttons
 * - Integration with upload overlay for real-time upload progress
 * - Order cancellation and editing capabilities
 */
import { FC } from 'react';
import { useLoaderData } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  Button,
  Popper,
  Paper,
  ClickAwayListener,
  Tooltip,
  Chip,
  styled,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { resolveLdtOrderDetailView } from '@ldt/features/orders/utils/resolve_ldt_order_detail_view';
import { OrderRepository } from 'src/api/order_repository';
import { ActivityLogsDropdown } from 'src/components/activity_logs_dropdown';
import { PageHeader } from 'src/components/page_header';
import { PageTitle } from 'src/components/page_title';
import { SectionCard } from 'src/components/section_card';
import { getPlatform } from 'src/config/environment';
import { orderKeys } from 'src/constants/orders';
import {
  useWorkflow,
  useWorkflowType,
} from 'src/distribution_platforms/features/hooks/use_workflow';
import {
  OrderDetailSection,
  getSectionsForColumn,
  OrderDetailSectionId,
  OrderDetailDisplayer,
} from 'src/distribution_platforms/features/order_details';
import { useActivityLogs } from 'src/hooks/use_activity_logs';
import { useUserRoles } from 'src/hooks/use_auth';
import { DistributionPlatform } from 'src/types/env';
import {
  isClinicAdmin,
  isClinicUser,
  isCustomerSuccessOrSalesUser,
  isOrderingPhysician,
  SpecimenTypeUtils,
} from 'src/utils';

const OrderDetailPageLayout = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  minHeight: 0,
});

const OrderDetailHeaderWrapper = styled(Box)({
  flexShrink: 0,
});

const OrderDetailHeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  minWidth: 0,
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(0),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(1.5),
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

const OrderDetailHeaderChips = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minWidth: 0,
  marginTop: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  [theme.breakpoints.up('md')]: {
    width: 'auto',
  },
}));

const OrderDetailSectionsRow = styled(Box)({
  display: 'flex',
  minWidth: 0,
});

const OrderDetailSectionsRowResponsive = styled(OrderDetailSectionsRow)(
  ({ theme }) => ({
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  }),
);

const OrderDetailMainColumn = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flex: 1,
    marginRight: 0,
  },
  [theme.breakpoints.up('md')]: {
    flex: 2,
    marginRight: theme.spacing(3),
  },
}));

const OrderDetailSideColumn = styled(Box)({
  flex: 1,
});

const OrderDetail: FC<{
  orderId: string;
  orderRepository: OrderRepository;
  orderDetailDisplayer: OrderDetailDisplayer;
  isBackgroundMode?: boolean;
}> = ({ orderId, orderRepository, orderDetailDisplayer, isBackgroundMode }) => {
  const theme = useTheme();
  const userRoles = useUserRoles();
  const workflowType = useWorkflowType();
  const workflow = useWorkflow();
  const displayContext = {
    userRoles,
    workflowType: workflowType,
    workflow: workflow,
  };

  const { data: order } = useSuspenseQuery({
    queryKey: orderKeys.detail(orderId),
    queryFn: () => {
      return orderRepository.getOrderById(orderId, {
        view: resolveLdtOrderDetailView(),
      });
    },
    refetchOnMount: !isBackgroundMode,
    refetchOnWindowFocus: !isBackgroundMode,
  });

  const {
    activityLogs,
    anchorEl: activityLogsAnchorEl,
    isOpen: activityLogsOpen,
    isLoading: activityLogsLoading,
    handleOpen: handleActivityLogsClick,
    handleClose: handleActivityLogsClose,
  } = useActivityLogs(orderId, orderRepository);

  if (!order) {
    return <Box>Order not found</Box>;
  }

  const renderHeader = (title: React.ReactNode, action?: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
      }}
    >
      {typeof title === 'string' ? (
        <Typography
          variant='subtitle1'
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {title}
        </Typography>
      ) : (
        title
      )}
      {action}
    </Box>
  );

  // Render section
  const renderSection = (section: OrderDetailSection) => {
    if (!section.shouldDisplay(order, displayContext)) {
      return null;
    }

    let headerAction: React.ReactNode;
    let sectionHeader: React.ReactNode = section.title;

    // Customize header based on section type
    if (section.id === OrderDetailSectionId.Information) {
      headerAction = (
        <Chip
          label={
            <Typography variant='body2'>
              {`Order Type: `}
              <Typography
                component='span'
                variant='body2'
                color='primary'
                sx={{
                  fontWeight: theme.typography.fontWeightMedium,
                }}
                data-testid={'order-type'}
              >
                {SpecimenTypeUtils.getSpecimenTypeName(order.specimenType)}
              </Typography>
            </Typography>
          }
          sx={{
            backgroundColor: theme.palette.action.hover,
          }}
        />
      );
    } else if (section.id === OrderDetailSectionId.Actions) {
      const platform = getPlatform();
      const showLdtActivityLogs =
        platform === DistributionPlatform.Ldt &&
        (isCustomerSuccessOrSalesUser() ||
          isOrderingPhysician() ||
          isClinicAdmin());
      const showSamdActivityLogs =
        platform === DistributionPlatform.Samd &&
        (isClinicUser() || isClinicAdmin());
      const showActivityLogs = showLdtActivityLogs || showSamdActivityLogs;

      headerAction = showActivityLogs ? (
        <Button size='large' onClick={handleActivityLogsClick}>
          ACTIVITY LOGS
        </Button>
      ) : undefined;
    } else if (section.id === OrderDetailSectionId.Status) {
      const statusName = workflow.getStatusDisplayName(order.status);

      sectionHeader = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography
            variant='subtitle1'
            sx={{
              fontWeight: theme.typography.fontWeightMedium,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              mr: 2,
            }}
          >
            Ordering Status:
          </Typography>
          <Tooltip title={statusName}>
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: theme.typography.fontWeightMedium,
                color: 'text.primary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {statusName}
            </Typography>
          </Tooltip>
        </Box>
      );
    }

    return (
      <SectionCard
        key={section.id}
        data-testid={section.id}
        header={renderHeader(sectionHeader, headerAction)}
        collapsible={section.collapsible}
        defaultExpanded={section.defaultExpanded}
        showContentAsHeader={section.showContentAsHeader}
      >
        {section.render(order, displayContext)}
      </SectionCard>
    );
  };

  // Get sections for each column
  const mainSections = getSectionsForColumn(
    orderDetailDisplayer.sections,
    order,
    'main',
    displayContext,
  );
  const sideSections = getSectionsForColumn(
    orderDetailDisplayer.sections,
    order,
    'side',
    displayContext,
  );

  const headerExtra = orderDetailDisplayer.renderHeaderExtra?.(
    order,
    displayContext,
  );

  const headerContent = (
    <>
      <PageTitle title={order.crossOrgId!} data-testid='order-number' />
      {headerExtra != null && (
        <OrderDetailHeaderChips>{headerExtra}</OrderDetailHeaderChips>
      )}
    </>
  );

  return (
    <OrderDetailPageLayout>
      <OrderDetailHeaderWrapper>
        <PageHeader>
          <OrderDetailHeaderContent>{headerContent}</OrderDetailHeaderContent>
        </PageHeader>
      </OrderDetailHeaderWrapper>
      <OrderDetailSectionsRowResponsive>
        <OrderDetailMainColumn>
          {mainSections.map(renderSection)}
        </OrderDetailMainColumn>
        <OrderDetailSideColumn>
          {sideSections.map(renderSection)}
        </OrderDetailSideColumn>
      </OrderDetailSectionsRowResponsive>
      {/* Activity Logs Popper */}
      <Popper
        open={activityLogsOpen}
        anchorEl={activityLogsAnchorEl}
        placement='bottom-end'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleActivityLogsClose}>
          <Paper sx={{ borderRadius: '10px', boxShadow: 3 }}>
            <ActivityLogsDropdown
              activityLogs={activityLogs}
              isLoading={activityLogsLoading}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </OrderDetailPageLayout>
  );
};

export const OrderDetailPage = ({
  orderRepository,
  orderDetailDisplayer,
  orderId: propOrderId,
  isBackgroundMode = false,
}: {
  orderRepository: OrderRepository;
  orderDetailDisplayer: OrderDetailDisplayer;
  orderId?: string;
  isBackgroundMode?: boolean;
}) => {
  const loaderData = useLoaderData() as { orderId: string } | null;
  const orderId = propOrderId || loaderData?.orderId;

  if (!orderId) {
    return <Box>Order ID not found</Box>;
  }

  return (
    <OrderDetail
      key={orderId}
      orderId={orderId}
      orderRepository={orderRepository}
      orderDetailDisplayer={orderDetailDisplayer}
      isBackgroundMode={isBackgroundMode}
    />
  );
};
